import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const emptyForm = { name: '', subject: '', body_html: '' };

const PractitionerEmailTemplates = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || user?.uuid || JSON.parse(localStorage.getItem('user') || '{}')?.uuid;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, navigate]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/practitioners/${userId}/email-templates`, { headers });
      setTemplates(response.data || []);
    } catch (err) {
      setError('Failed to fetch email templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (template) => {
    setEditingId(template.id);
    setFormData({ name: template.name, subject: template.subject, body_html: template.body_html });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await axios.put(`${apiUrl}/practitioners/${userId}/email-templates/${editingId}`, formData, { headers });
      } else {
        await axios.post(`${apiUrl}/practitioners/${userId}/email-templates`, formData, { headers });
      }
      setShowForm(false);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    setError('');
    try {
      await axios.delete(`${apiUrl}/practitioners/${userId}/email-templates/${templateId}`, { headers });
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  if (loading) return <div className="page-container">Loading email templates...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Email Templates</h1>
        <div className="flex-between">
          <button className="btn btn-secondary" onClick={() => navigate('/practitioner/campaigns')}>View Campaigns</button>
          <button className="btn btn-primary" onClick={openCreate}>+ New Template</button>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <p className="text-muted" style={{ marginTop: -8, marginBottom: 14 }}>
        Use <code>{'{{client_name}}'}</code>, <code>{'{{client_email}}'}</code>, and <code>{'{{practitioner_name}}'}</code> in
        your subject or body — they're filled in per-client when a campaign is sent.
      </p>

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Subject</th><th></th></tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id}>
                <td><strong>{t.name}</strong></td>
                <td>{t.subject}</td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(t)} style={{ marginRight: 6 }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {templates.length === 0 && <tr><td colSpan="3" className="empty-state">No templates yet — create your first one above.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Template' : 'New Template'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Template Name</label>
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Subject</label>
                <input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Body (HTML)</label>
                <textarea
                  rows="8"
                  value={formData.body_html}
                  onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block">{editingId ? 'Save Changes' : 'Create Template'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PractitionerEmailTemplates;
